import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import StayEnhancementsSection from "@/components/StayEnhancementsSection";
import BookeoToursSection from "@/components/BookeoToursSection";
import { getEnhancePageContent } from "@/lib/enhance-page.functions";
import { responsiveImg } from "@/lib/responsive-image";
import heroFallback from "@/assets/enhance-hero-couple.jpg.asset.json";
import type { CategoryComponentProps } from "./index";

export default function EnhanceCategoryPage(_props: CategoryComponentProps) {
  const fetchContent = useServerFn(getEnhancePageContent);
  const { data } = useQuery({
    queryKey: ["enhance-page"],
    queryFn: () => fetchContent(),
    staleTime: 60_000,
  });

  const heroImage = data?.hero_image_url || heroFallback.url;
  const eyebrow = data?.hero_eyebrow || "Enhance";
  const title = data?.hero_title || "Enhance Your Stay";
  const subtitle =
    data?.hero_subtitle ||
    "Curated packages and guided tours to elevate every moment.";

  const hero = responsiveImg(heroImage, "100vw");

  return (
    <div className="bg-background text-foreground min-h-full">
      <section className="mobile-hero relative w-full overflow-hidden vignette aspect-[4/5] sm:aspect-[16/10] max-h-[78svh]">
        <img
          src={hero.src}
          srcSet={hero.srcSet}
          sizes={hero.sizes}
          alt={title}
          decoding="async"
          fetchPriority="high"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
        <div className="mobile-hero-scrim" />
        <div className="mobile-hero-copy px-7 flex flex-col items-start">
          <div className="mobile-hero-kicker text-[10px] tracking-[0.4em] uppercase mb-3 text-shadow-soft">
            {eyebrow}
          </div>
          <h1 className="mobile-hero-title font-serif text-4xl sm:text-5xl leading-[1.05] text-shadow-soft max-w-[16ch] whitespace-pre-line">
            {title}
          </h1>
          {subtitle && (
            <p className="mobile-hero-subtitle mt-3 text-sm sm:text-base font-light max-w-[42ch] text-shadow-soft">
              {subtitle}
            </p>
          )}
        </div>
      </section>

      <StayEnhancementsSection
        eyebrow="Stay Enhancements"
        title="Make it your own"
        subtitle={`Tap any card for details and photos.\nAdd at booking or call to add to existing reservations.`}
        className="bg-background px-4 sm:px-6 py-12 sm:py-16"
      />

      <BookeoToursSection />
    </div>
  );
}
